import { useState, useEffect, useRef } from "react"
import { useQuery, useMutation } from "@tanstack/react-query"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { useToast } from "@/hooks/use-toast"
import { apiRequest, queryClient } from "@/lib/queryClient"
import { getSuggestedTimes } from "@/lib/postingSchedule"
import {
  Check,
  X,
  Edit2,
  Hash,
  Loader2,
  AlertCircle,
  TestTube2,
  ExternalLink,
  Lightbulb,
  Clock,
  CalendarClock,
  RotateCw,
} from "lucide-react"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { FaLinkedin, FaFacebook, FaInstagram } from "react-icons/fa"
import { formatDateTime } from "@/lib/dates"
import { isValidTitle, extractTitleFromContent } from "@/lib/postTitle"
import { statusConfig } from "@/lib/status-config"
import { SchedulePopover } from "@/components/SchedulePopover"
import { RelevanceScore } from "@/components/RelevanceScore"
import { ScheduledPlanLink } from "@/components/ScheduledPlanLink"
import type { IndustryNews, RepurposingContent, NapkinWisdom, PillarPost, GeneratedPost } from "@shared/schema"

// Simple markdown renderer for bold text and basic formatting
function renderMarkdown(text: string): JSX.Element[] {
  const elements: JSX.Element[] = []
  const lines = text.split('\n')
  
  lines.forEach((line, lineIndex) => {
    // Process bold text (**text** or __text__)
    const parts = line.split(/(\*\*[^*]+\*\*|__[^_]+__)/g)
    const processedParts = parts.map((part, partIndex) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={`${lineIndex}-${partIndex}`}>{part.slice(2, -2)}</strong>
      }
      if (part.startsWith('__') && part.endsWith('__')) {
        return <strong key={`${lineIndex}-${partIndex}`}>{part.slice(2, -2)}</strong>
      }
      return <span key={`${lineIndex}-${partIndex}`}>{part}</span>
    })
    
    elements.push(
      <span key={lineIndex}>
        {processedParts}
        {lineIndex < lines.length - 1 && <br />}
      </span>
    )
  })
  
  return elements
}

type ContentItem = IndustryNews | RepurposingContent | NapkinWisdom | PillarPost

interface NewsPostsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  newsItem: ContentItem
  sourceType?: 'industry_news' | 'repurposing_content' | 'napkin_wisdom' | 'pillar_post'
  /** Platform tab to show first (deep-links from the Posting-Plan pass the post's platform). */
  initialPlatform?: string
  /** View-only: hide all edit/approve/publish/schedule controls and the Test-Modus toggle.
   *  Used for DFY (Lina) items on the Repurposing page — they're locked after email approval,
   *  so this surface only shows the post + where it's scheduled. */
  readOnly?: boolean
}

const platformConfig = {
  linkedin: {
    name: "LinkedIn",
    icon: FaLinkedin,
    color: "bg-blue-600",
    textColor: "text-blue-600",
    borderColor: "border-blue-600"
  },
  facebook: {
    name: "Facebook",
    icon: FaFacebook,
    color: "bg-blue-500",
    textColor: "text-blue-500",
    borderColor: "border-blue-500"
  },
  instagram: {
    name: "Instagram",
    icon: FaInstagram,
    color: "bg-pink-500",
    textColor: "text-pink-500",
    borderColor: "border-pink-500"
  }
}

// Which list query a publish/schedule must refresh, per source module
const sourceListKey: Record<string, string> = {
  industry_news: '/api/industry-news',
  napkin_wisdom: '/api/napkin-wisdom',
  repurposing_content: '/api/repurposing-content',
  pillar_post: '/api/pillar-posts',
}

export function NewsPostsModal({ open, onOpenChange, newsItem, sourceType = 'industry_news', initialPlatform, readOnly = false }: NewsPostsModalProps) {
  const { toast } = useToast()
  // True while a publish was triggered by the OAuth auto-resume effect (not a click) —
  // its 403 handler must not redirect again, or the user loops between this modal and
  // /platform-connections for as long as the account stays unconnected.
  const autoResumeRef = useRef(false)
  const [activeTab, setActiveTab] = useState<string>(initialPlatform || "linkedin")
  const [editingPost, setEditingPost] = useState<string | null>(null)
  const [editTitle, setEditTitle] = useState<string>("")
  const [editContent, setEditContent] = useState<string>("")
  const [editHashtags, setEditHashtags] = useState<string>("")
  
  // Local state for pillar post data (to allow updates without closing modal)
  const [localPillarPostData, setLocalPillarPostData] = useState<PillarPost | null>(
    sourceType === 'pillar_post' ? newsItem as PillarPost : null
  )
  
  // Sync local pillar post data when newsItem changes
  useEffect(() => {
    if (sourceType === 'pillar_post') {
      setLocalPillarPostData(newsItem as PillarPost)
    }
  }, [newsItem, sourceType])
  
  // Type guard to check if item is IndustryNews
  const isIndustryNews = (item: ContentItem): item is IndustryNews => {
    return 'sourceUrl' in item
  }
  
  // Type guard to check if item is NapkinWisdom (must have episodeNumber which is unique to NapkinWisdom)
  const isNapkinWisdom = (item: ContentItem): item is NapkinWisdom => {
    return sourceType === 'napkin_wisdom'
  }
  
  // Check if item has a video (safe access with optional chaining)
  const hasVideo = sourceType === 'napkin_wisdom' && 'videoUrl' in newsItem && !!(newsItem as NapkinWisdom).videoUrl
  
  // Test Mode state (persisted in localStorage, scoped per module — slice-4: the global key bled
  // a test toggle from one module into every other module's modal) - with SSR guard
  const testModeKey = `lateApiTestMode:${sourceType}`
  const [testMode, setTestMode] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    try {
      return localStorage.getItem(testModeKey) === 'true'
    } catch {
      return false
    }
  })
  
  // Persist test mode to localStorage - with SSR guard
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(testModeKey, testMode.toString())
      } catch {
        /* storage blocked */
      }
    }
  }, [testMode, testModeKey])

  // A publish/schedule changes more than this modal's scoped query: the scheduled list
  // (conflict detection), the posting plan, and the source module's list (derived article
  // status + stats) would otherwise stay stale for up to their staleTime (audit A7).
  const invalidateAfterPublish = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/generated-posts', sourceType, newsItem.id] })
    queryClient.invalidateQueries({ queryKey: ['/api/generated-posts/scheduled'] })
    queryClient.invalidateQueries({ queryKey: ['/api/generated-posts/publishing-plan'] })
    const listKey = sourceListKey[sourceType]
    if (listKey) {
      queryClient.invalidateQueries({ queryKey: [listKey] })
      // Deep-link single-item caches (?news= / ?item=) hold the same derived status/schedule
      // this publish just changed; re-following the link would otherwise show stale fields.
      queryClient.invalidateQueries({ queryKey: [`${listKey}/item`, newsItem.id] })
    }
  }

  // Schedule mutation — same as publish but with scheduledFor
  const schedulePostMutation = useMutation({
    mutationFn: async ({ postId, platform, scheduledFor }: { postId: string; platform: string; scheduledFor: Date }) => {
      const endpoint = sourceType === 'pillar_post'
        ? `/api/pillar-posts/${postId}/publish`
        : `/api/generated-posts/${postId}/publish`
      return apiRequest("POST", endpoint, {
        isDraft: false,
        platform,
        scheduledFor: scheduledFor.toISOString(),
      })
    },
    onSuccess: () => {
      invalidateAfterPublish()
      toast({
        title: "Post geplant!",
        description: "Der Post wurde erfolgreich eingeplant.",
      })
      // Pillar modal holds a frozen snapshot that won't reflect the new scheduled state;
      // close so a second click can't re-schedule (a second live Late post). Other sources
      // keep the modal open to act on multiple platforms.
      if (sourceType === 'pillar_post') onOpenChange(false)
    },
    onError: (error: any) => {
      if (error.status === 403 && error.data?.requiresConnection) {
        const platform = error.data.platform || 'unbekannt'
        // Redirect to connect, but WITHOUT pendingPublishPostId: auto-resume would
        // publish immediately instead of re-scheduling the chosen time.
        toast({
          title: "Plattform verbinden",
          description: `Bitte verbinden Sie Ihr ${platform}-Konto und planen Sie den Beitrag danach erneut.`,
        })
        setTimeout(() => {
          window.location.href = error.data.redirectUrl || '/platform-connections'
        }, 1500)
        return
      }
      const errorDetails = error.data?.error || error.data?.message || error.message || "Unbekannter Fehler"
      toast({
        title: "Fehler beim Planen",
        description: errorDetails,
        variant: "destructive",
      })
    },
  })

  const handleSchedule = (post: GeneratedPost, scheduledFor: Date) => {
    const postId = sourceType === 'pillar_post' && post.sourceId ? post.sourceId : post.id
    const platform = post.platform || 'linkedin'
    schedulePostMutation.mutate({ postId, platform, scheduledFor })
  }


  // Fetch scheduled posts for conflict detection
  const { data: scheduledPosts = [] } = useQuery<GeneratedPost[]>({
    queryKey: ['/api/generated-posts/scheduled'],
    queryFn: async () => {
      const response = await fetch('/api/generated-posts/scheduled')
      if (!response.ok) return []
      return response.json()
    },
    enabled: open,
    staleTime: 30000,
  })

  // Build a map of scheduled times per platform for the suggestion engine
  const scheduledTimesByPlatform = scheduledPosts.reduce((acc, post) => {
    if (post.scheduledFor) {
      const p = post.platform || 'linkedin'
      if (!acc[p]) acc[p] = []
      acc[p].push(new Date(post.scheduledFor))
    }
    return acc
  }, {} as Record<string, Date[]>)

  // Fetch generated posts for this content item
  // For pillar_post, the newsItem itself IS the post, so we don't need to fetch
  const { data: posts = [], isLoading, error } = useQuery<GeneratedPost[]>({
    queryKey: ['/api/generated-posts', sourceType, newsItem.id],
    queryFn: async () => {
      const response = await fetch(`/api/generated-posts?sourceType=${sourceType}&sourceId=${newsItem.id}`)
      if (!response.ok) throw new Error('Failed to fetch posts')
      return response.json()
    },
    enabled: open && sourceType !== 'pillar_post',
    staleTime: 60000 // Cache for 1 minute to avoid refetching
  })

  // Auto-plan: schedule every still-draft post of this item at its platform's next free
  // optimal slot in one click. Reuses the exact per-post schedule mutation (so the
  // connect-redirect and conflict handling stay identical to a manual "Planen").
  const [autoScheduling, setAutoScheduling] = useState(false)
  const autoPlanEligible = sourceType !== 'pillar_post' && !readOnly
    ? posts.filter(p =>
        p.postingStatus !== 'posted' && p.postingStatus !== 'scheduled' &&
        p.approvalStatus !== 'rejected' && !!p.postContent)
    : []
  const handleAutoScheduleAll = async () => {
    if (!autoPlanEligible.length || autoScheduling) return
    setAutoScheduling(true)
    // Slots chosen in THIS batch must count as taken for the next post on the same platform.
    const chosenByPlatform: Record<string, Date[]> = {}
    try {
      for (const post of autoPlanEligible) {
        const platform = post.platform || 'linkedin'
        const existing = [
          ...(scheduledTimesByPlatform[platform] || []),
          ...(chosenByPlatform[platform] || []),
        ]
        const slot = getSuggestedTimes(platform as any, existing).find(s => !s.isTaken)
        if (!slot) continue
        await schedulePostMutation.mutateAsync({ postId: post.id, platform, scheduledFor: slot.date })
        ;(chosenByPlatform[platform] ||= []).push(slot.date)
      }
    } catch {
      // Per-mutation onError already toasts (and handles the connect-redirect); stop the batch.
    } finally {
      setAutoScheduling(false)
    }
  }
  
  // Pillar posts publish to their single stored platform (default LinkedIn); the server
  // ignores any client-selected platform (content.ts publish reads post.platform, not the
  // request body). So render exactly that one platform — never the 3-platform fan-out, which
  // misled users into thinking clicking the Facebook/Instagram tab changed the target network.
  // Normalize + clamp to a known platformConfig key: `platform` is free-text in the DB and some
  // writers store mixed case (e.g. "LinkedIn"); an unmatched key would render a blank tab pane.
  const pillarPlatform = (() => {
    const p = (localPillarPostData?.platform || 'linkedin').toLowerCase()
    return p in platformConfig ? p : 'linkedin'
  })()
  const pillarPlatforms: ReadonlyArray<string> =
    localPillarPostData ? [pillarPlatform] : ['linkedin']
  // readOnly (DFY view) surfaces the pillar row's own lifecycle as postingStatus/scheduledFor so
  // the status badge + "Geplant für <Datum>" deep-link (ScheduledPlanLink) render. This is scoped
  // to readOnly ONLY — the non-readOnly Wochenplan modal keeps its exact prior shape (no
  // postingStatus/scheduledFor added), so its action-button logic is unchanged.
  const pillarReadOnlyLifecycle = readOnly && localPillarPostData ? {
    postingStatus:
      localPillarPostData.status === 'scheduled' ? 'scheduled' :
      localPillarPostData.status === 'posted' ? 'posted' : undefined,
    scheduledFor: localPillarPostData.scheduledFor,
    postedAt: localPillarPostData.postedAt,
  } : {}
  const pillarPostAsGenerated: GeneratedPost[] = sourceType === 'pillar_post' && localPillarPostData ?
    pillarPlatforms.map(platform => ({
      id: `${localPillarPostData.id}-${platform}`,
      platform: platform,
      postTitle: isValidTitle(localPillarPostData.title) ? localPillarPostData.title! : extractTitleFromContent(localPillarPostData.postContent),
      postContent: localPillarPostData.postContent,
      hashtags: localPillarPostData.hashtags as string[] || [],
      approvalStatus: localPillarPostData.status,
      ...pillarReadOnlyLifecycle,
      sourceType: 'pillar_post',
      sourceId: localPillarPostData.id,
      createdAt: localPillarPostData.createdAt,
    } as GeneratedPost)) : []
  
  // Use pillar post data or fetched posts - apply title extraction fallback for all posts
  const effectivePosts = (sourceType === 'pillar_post' ? pillarPostAsGenerated : posts).map(post => ({
    ...post,
    postTitle: isValidTitle(post.postTitle) ? post.postTitle! : extractTitleFromContent(post.postContent)
  }))

  // Group posts by platform
  const postsByPlatform = effectivePosts.reduce((acc, post) => {
    acc[post.platform] = post
    return acc
  }, {} as Record<string, GeneratedPost>)

  // Update active tab when posts load - set to first available platform
  const availablePlatforms = Object.keys(postsByPlatform)
  
  // Effect to set initial tab to first platform with content
  useEffect(() => {
    if (effectivePosts.length > 0 && availablePlatforms.length > 0) {
      // Only update if current tab has no content
      if (!postsByPlatform[activeTab]) {
        setActiveTab(availablePlatforms[0])
      }
    }
  }, [effectivePosts.length, availablePlatforms.length, activeTab, postsByPlatform, availablePlatforms])

  // Reject mutation (updates approval status without publishing)
  const rejectPostMutation = useMutation({
    mutationFn: async ({ postId, approval }: {
      postId: string
      approval: { approvalStatus: "rejected"; approvalNotes?: string }
    }) => {
      // Pillar posts have a dedicated reject route that writes pillar_posts.status='rejected';
      // the generated-posts review route (which takes approvalStatus in the body) 404s on a
      // pillar id — that was the "Ablehnen always fails" bug.
      if (sourceType === 'pillar_post') {
        return apiRequest("POST", `/api/pillar-posts/${postId}/reject`, {})
      }
      return apiRequest("POST", `/api/generated-posts/${postId}/approve`, approval)
    },
    onSuccess: () => {
      if (sourceType === 'pillar_post') {
        // Pillar reject changes the pillar row's status → refresh the pillar list, and close
        // the modal (its frozen snapshot would otherwise show the pre-reject state).
        queryClient.invalidateQueries({ queryKey: ['/api/pillar-posts'] })
        onOpenChange(false)
      } else {
        // Only invalidate scoped queries to prevent modal close
        queryClient.invalidateQueries({ queryKey: ['/api/generated-posts', sourceType, newsItem.id] })
      }
      toast({
        title: "Post abgelehnt",
        description: "Der Post wurde abgelehnt.",
      })
    },
    onError: (error: any) => {
      toast({
        title: "Fehler",
        description: error.message || "Beim Ablehnen des Posts ist ein Fehler aufgetreten.",
        variant: "destructive",
      })
    }
  })

  const handleReject = (post: GeneratedPost) => {
    // For pillar posts, use sourceId (original ID) since the display ID includes platform suffix
    const postId = sourceType === 'pillar_post' && post.sourceId ? post.sourceId : post.id
    rejectPostMutation.mutate({
      postId,
      approval: { approvalStatus: "rejected" }
    })
  }

  // Publish mutation (Late API posting) - calls Late.dev publishing endpoint
  const publishPostMutation = useMutation({
    mutationFn: async ({ postId, platform }: { postId: string; platform: string }) => {
      // Use different endpoint for pillar posts
      const endpoint = sourceType === 'pillar_post' 
        ? `/api/pillar-posts/${postId}/publish`
        : `/api/generated-posts/${postId}/publish`
      return apiRequest("POST", endpoint, { 
        isDraft: testMode,  // Test mode = draft mode in Late.dev
        platform: platform  // Selected platform from active tab
      })
    },
    onSuccess: (data) => {
      autoResumeRef.current = false
      invalidateAfterPublish()

      if (testMode) {
        toast({
          title: "Test-Post erstellt!",
          description: "Der Post wurde als Entwurf gespeichert (wird nicht veröffentlicht).",
        })
      } else {
        toast({
          title: "Post veröffentlicht!",
          description: "Der Post wurde erfolgreich veröffentlicht.",
        })
      }
      // Pillar modal holds a frozen snapshot that won't flip to "posted"; close so a second
      // click can't republish (a second live Late post). Other sources keep the modal open.
      if (sourceType === 'pillar_post') onOpenChange(false)
    },
    onError: (error: any) => {
      // Platform not connected: send the user to the connect page and auto-resume the
      // publish after OAuth (same flow as PhotoPreviewModal).
      if (error.status === 403 && error.data?.requiresConnection) {
        const platform = error.data.platform || 'unbekannt'
        if (autoResumeRef.current) {
          // The auto-resumed publish itself failed on the still-unconnected platform —
          // do NOT redirect again (see autoResumeRef above), just say what's wrong.
          autoResumeRef.current = false
          toast({
            title: `${platform.charAt(0).toUpperCase() + platform.slice(1)} nicht verbunden`,
            description: "Bitte verbinden Sie Ihr Konto unter 'Plattform verbinden'.",
            variant: "destructive",
          })
          return
        }
        sessionStorage.setItem('pendingPublishPostId', error.data.postId || '')
        toast({
          title: "Plattform verbinden",
          description: `Bitte verbinden Sie Ihr ${platform}-Konto, um fortzufahren.`,
        })
        setTimeout(() => {
          window.location.href = error.data.redirectUrl || '/platform-connections'
        }, 1500)
        return
      }

      // Show detailed error info for debugging
      const errorDetails = error.data?.error || error.data?.message || error.message || "Unbekannter Fehler"
      const statusCode = error.status ? `${error.status}: ` : ""

      toast({
        title: "Fehler",
        description: `${statusCode}${errorDetails}`,
        variant: "destructive",
      })
    }
  })

  const handlePublish = (post: GeneratedPost) => {
    // For pillar posts, use sourceId (original ID) since the display ID includes platform suffix
    const postId = sourceType === 'pillar_post' && post.sourceId ? post.sourceId : post.id
    // Use the platform from the post (which reflects the active tab for pillar posts)
    const platform = post.platform || 'linkedin'
    publishPostMutation.mutate({ postId, platform })
  }

  // A live pillar publish is irreversible and one-click; confirm intent first. Test-mode
  // drafts are reversible, so skip the prompt there. Other sources keep one-click publish.
  const [pendingPublishPost, setPendingPublishPost] = useState<GeneratedPost | null>(null)
  const requestPublish = (post: GeneratedPost) => {
    if (sourceType === 'pillar_post' && !testMode) {
      setPendingPublishPost(post)
    } else {
      handlePublish(post)
    }
  }

  // Auto-resume a publish that was interrupted by the OAuth connect flow (403 above)
  useEffect(() => {
    if (open && effectivePosts.length > 0) {
      const pendingPostId = sessionStorage.getItem('pendingPublishPostId')
      if (pendingPostId) {
        // Pillar posts store their real id server-side but carry a synthetic
        // `${id}-${platform}` id in this modal — match via sourceId as well.
        const pendingPost = effectivePosts.find(p =>
          p.id === pendingPostId || (sourceType === 'pillar_post' && p.sourceId === pendingPostId)
        )
        if (pendingPost) {
          sessionStorage.removeItem('pendingPublishPostId')
          autoResumeRef.current = true
          handlePublish(pendingPost)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, posts])

  // Edit mutation - handles both generated posts and pillar posts
  const editPostMutation = useMutation({
    mutationFn: async ({ postId, updates }: {
      postId: string
      updates: { postTitle?: string; postContent?: string; hashtags?: string[] }
    }) => {
      if (sourceType === 'pillar_post') {
        // Pillar posts use PUT with different field names
        return apiRequest("PUT", `/api/pillar-posts/${postId}`, {
          title: updates.postTitle,
          postContent: updates.postContent,
          hashtags: updates.hashtags,
        })
      }
      // All other source types use generated-posts endpoint
      return apiRequest("PATCH", `/api/generated-posts/${postId}`, updates)
    },
    onSuccess: (_, variables) => {
      // Invalidate the appropriate query based on source type
      if (sourceType === 'pillar_post') {
        queryClient.invalidateQueries({ queryKey: ['/api/pillar-posts'] })
        // Update local state so modal shows the changes without closing
        if (localPillarPostData) {
          setLocalPillarPostData({
            ...localPillarPostData,
            title: variables.updates.postTitle || localPillarPostData.title,
            postContent: variables.updates.postContent || localPillarPostData.postContent,
            hashtags: variables.updates.hashtags || localPillarPostData.hashtags,
          })
        }
      } else {
        queryClient.invalidateQueries({ queryKey: ['/api/generated-posts', sourceType, newsItem.id] })
      }
      setEditingPost(null)
      setEditTitle("")
      setEditContent("")
      setEditHashtags("")
      toast({
        title: "Änderungen gespeichert",
        description: "Der Post wurde erfolgreich aktualisiert.",
      })
    },
    onError: (error: any) => {
      toast({
        title: "Fehler",
        description: error.message || "Beim Speichern ist ein Fehler aufgetreten.",
        variant: "destructive",
      })
    }
  })

  const handleEdit = (post: GeneratedPost) => {
    setEditingPost(post.id)
    // Use valid title or extract from content
    const extractedTitle = extractTitleFromContent(post.postContent)
    setEditTitle(isValidTitle(post.postTitle) ? post.postTitle! : extractedTitle)
    setEditContent(post.postContent || "")
    setEditHashtags(post.hashtags?.join(", ") || "")
  }

  const handleSaveEdit = (post: GeneratedPost) => {
    // For pillar posts, use sourceId (original ID) since the display ID includes platform suffix
    const postId = sourceType === 'pillar_post' && post.sourceId ? post.sourceId : post.id
    
    // Parse hashtags from comma-separated string
    const hashtagsArray = editHashtags
      .split(",")
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0)
      .map(tag => tag.startsWith("#") ? tag : `#${tag}`)

    editPostMutation.mutate({
      postId,
      updates: {
        postTitle: editTitle || undefined,
        postContent: editContent,
        hashtags: hashtagsArray.length > 0 ? hashtagsArray : undefined
      }
    })
  }

  const handleCancelEdit = () => {
    setEditingPost(null)
    setEditTitle("")
    setEditContent("")
    setEditHashtags("")
  }

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="flex flex-row items-center justify-between space-y-0">
          <DialogTitle>{readOnly ? "Post-Vorschau" : "Post Vorschau & Freigabe"}</DialogTitle>

          {!readOnly && (
          <div className="flex items-center space-x-3">
            {testMode && (
              <Badge variant="secondary" className="gap-1.5">
                <TestTube2 className="h-3 w-3" />
                Test Modus
              </Badge>
            )}
            <div className="flex items-center space-x-2">
              <Switch
                id="test-mode"
                checked={testMode}
                onCheckedChange={setTestMode}
                data-testid="switch-test-mode"
              />
              <Label htmlFor="test-mode" className="text-sm cursor-pointer">
                Test Modus
              </Label>
            </div>
          </div>
          )}
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Source Preview - for pillar posts, use local state for live updates */}
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-semibold text-sm line-clamp-2">
                    {sourceType === 'pillar_post' && localPillarPostData
                      ? (localPillarPostData.title || extractTitleFromContent(localPillarPostData.postContent))
                      : (newsItem.title || 'Ohne Titel')}
                  </h3>
                  {'relevanceScore' in newsItem && newsItem.relevanceScore != null && (
                    <div className="flex-shrink-0">
                      <RelevanceScore score={newsItem.relevanceScore} />
                    </div>
                  )}
                </div>
                
                {'summary' in newsItem && newsItem.summary && (
                  <p className="text-sm text-muted-foreground line-clamp-4">
                    {newsItem.summary}
                  </p>
                )}
                
                {sourceType === 'pillar_post' && localPillarPostData?.pillarName && (
                  <Badge variant="secondary" className="text-xs">
                    {localPillarPostData.pillarName}
                  </Badge>
                )}
                
                {sourceType !== 'pillar_post' && 'pillarName' in newsItem && newsItem.pillarName && (
                  <Badge variant="secondary" className="text-xs">
                    {newsItem.pillarName}
                  </Badge>
                )}

                {/* Video for Napkin Wisdom */}
                {hasVideo && (
                  <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
                    <video
                      src={(() => {
                        // Transform Dropbox URLs for direct browser playback
                        const url = (newsItem as NapkinWisdom).videoUrl!;
                        if (url.includes('dropbox.com')) {
                          let playableUrl = url
                            .replace(/[?&]dl=0/, '?raw=1')
                            .replace(/[?&]dl=1/, '?raw=1');
                          if (!playableUrl.includes('raw=1')) {
                            playableUrl = playableUrl + (playableUrl.includes('?') ? '&raw=1' : '?raw=1');
                          }
                          return playableUrl;
                        }
                        return url;
                      })()}
                      controls
                      className="w-full h-full object-contain"
                      preload="metadata"
                    />
                  </div>
                )}
                
                {/* Image fallback - show when no video */}
                {!hasVideo && 'imageUrl' in newsItem && newsItem.imageUrl && (
                  <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
                    <img
                      src={newsItem.imageUrl}
                      alt={newsItem.title}
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
                
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
                  <span>{formatDateTime(newsItem.createdAt!)}</span>
                  {isIndustryNews(newsItem) && newsItem.sourceUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(newsItem.sourceUrl!, '_blank', 'noopener,noreferrer')}
                      className="h-7 gap-1"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Quelle
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {sourceType === 'pillar_post' && !readOnly && (
              <Alert className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
                <Lightbulb className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
                  <span className="font-semibold">LinkedIn-Empfehlung:</span> Pillar Posts performen am besten auf LinkedIn. Sie können den Beitrag aber auch auf anderen Plattformen teilen, wenn er Ihnen besonders wichtig ist.
                </AlertDescription>
              </Alert>
            )}
            {readOnly && (
              <Alert className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
                <Lightbulb className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
                  Dieser Beitrag wurde von Lina im Wochenplan erstellt und ist bereits freigegeben. Bearbeitung & Freigabe erfolgen im Wochenplan.
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Posts by Platform */}
          <div className="space-y-4">
            {isLoading && sourceType !== 'pillar_post' ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : error && sourceType !== 'pillar_post' ? (
              <div className="text-center py-12">
                <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                <p className="text-sm text-destructive">Fehler beim Laden der Beiträge. Bitte versuchen Sie es später erneut.</p>
              </div>
            ) : effectivePosts.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">
                  Ihre Beiträge werden gerade erstellt. Das dauert in der Regel nur wenige Minuten.
                </p>
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                {autoPlanEligible.length > 0 && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full mb-2 gap-2"
                    onClick={handleAutoScheduleAll}
                    disabled={autoScheduling}
                    data-testid="button-auto-plan-all"
                  >
                    {autoScheduling
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <CalendarClock className="h-4 w-4" />}
                    Alle {autoPlanEligible.length} Posts automatisch planen
                  </Button>
                )}
                {/* readOnly (DFY) shows a single LinkedIn post — no platform switcher needed */}
                {!readOnly && (
                <TabsList className="grid w-full grid-cols-3">
                  {Object.entries(platformConfig).map(([platform, config]) => {
                    const post = postsByPlatform[platform]
                    const Icon = config.icon
                    return (
                      <TabsTrigger
                        key={platform}
                        value={platform}
                        disabled={!post}
                        className="gap-2"
                      >
                        <Icon className="h-4 w-4" />
                        {config.name}
                      </TabsTrigger>
                    )
                  })}
                </TabsList>
                )}

                {Object.entries(platformConfig).map(([platform, config]) => {
                  const post = postsByPlatform[platform]
                  if (!post) return null

                  // Use postingStatus if it's meaningful (scheduled/posted), otherwise fall back to approvalStatus
                  const effectiveStatus = post.postingStatus === "scheduled" || post.postingStatus === "posted"
                    ? post.postingStatus
                    : post.approvalStatus
                  const status = statusConfig[effectiveStatus ?? "pending"] || statusConfig.pending
                  const isEditing = editingPost === post.id
                  const isScheduled = post.postingStatus === "scheduled"
                  const isPosted = post.postingStatus === "posted"
                  const scheduledTime = post.scheduledFor

                  return (
                    <TabsContent key={platform} value={platform} className="space-y-4">
                      <Card>
                        <CardContent className="p-4 space-y-4">
                          {/* Status Badge */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`p-2 rounded-lg ${config.color}`}>
                                <config.icon className="h-4 w-4 text-white" />
                              </div>
                              <span className="font-medium">{config.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {(isScheduled || isPosted) && scheduledTime && (
                                <ScheduledPlanLink
                                  postId={sourceType === 'pillar_post' && post.sourceId ? post.sourceId : post.id}
                                  scheduledFor={scheduledTime}
                                  label={isPosted ? "Veröffentlicht am" : "Geplant für"}
                                  onBeforeNavigate={() => onOpenChange(false)}
                                  testId="badge-scheduled-deeplink"
                                />
                              )}
                              <Badge className={status.color}>
                                {status.label}
                              </Badge>
                            </div>
                          </div>

                          {/* Post Title */}
                          <div>
                            <h4 className="font-semibold text-sm text-muted-foreground mb-1">Titel</h4>
                            {isEditing ? (
                              <Input
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                placeholder="Post-Titel (optional)"
                                data-testid="input-post-title"
                                autoFocus
                              />
                            ) : (
                              <p className="font-medium">
                                {isValidTitle(post.postTitle) ? post.postTitle : extractTitleFromContent(post.postContent)}
                              </p>
                            )}
                          </div>

                          {/* Post Media (Video or Image from N8N) */}
                          {post.imageContent && (
                            <div>
                              <h4 className="font-semibold text-sm text-muted-foreground mb-1">
                                {sourceType === 'napkin_wisdom' ? 'Video' : 'Bild'}
                              </h4>
                              <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
                                {sourceType === 'napkin_wisdom' ? (
                                  <video
                                    src={(() => {
                                      // Transform Dropbox URLs for direct browser playback
                                      const url = post.imageContent;
                                      if (url.includes('dropbox.com')) {
                                        let playableUrl = url
                                          .replace(/[?&]dl=0/, '?raw=1')
                                          .replace(/[?&]dl=1/, '?raw=1');
                                        if (!playableUrl.includes('raw=1')) {
                                          playableUrl = playableUrl + (playableUrl.includes('?') ? '&raw=1' : '?raw=1');
                                        }
                                        return playableUrl;
                                      }
                                      return url;
                                    })()}
                                    controls
                                    className="w-full h-full object-contain"
                                    preload="metadata"
                                  />
                                ) : (
                                  <img
                                    src={post.imageContent}
                                    alt={post.postTitle || 'Post-Bild'}
                                    className="w-full h-full object-contain"
                                    onError={(e) => {
                                      console.log('Image load error, trying external URL...');
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                          )}

                          {/* Post Content */}
                          <div>
                            <h4 className="font-semibold text-sm text-muted-foreground mb-1">Inhalt</h4>
                            {isEditing ? (
                              <Textarea
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                className="min-h-[200px] resize-none"
                                data-testid="textarea-post-content"
                              />
                            ) : (
                              <div className="text-sm whitespace-pre-wrap">{renderMarkdown(post.postContent || '')}</div>
                            )}
                          </div>

                          {/* Hashtags */}
                          <div>
                            <h4 className="font-semibold text-sm text-muted-foreground mb-2">Hashtags</h4>
                            {isEditing ? (
                              <Input
                                value={editHashtags}
                                onChange={(e) => setEditHashtags(e.target.value)}
                                placeholder="Hashtags (getrennt durch Komma)"
                                data-testid="input-post-hashtags"
                              />
                            ) : (
                              post.hashtags && post.hashtags.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {post.hashtags.map((tag, index) => (
                                    <Badge key={index} variant="outline" className="gap-1">
                                      <Hash className="h-3 w-3" />
                                      {tag.replace('#', '')}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground">(Keine Hashtags)</p>
                              )
                            )}
                          </div>

                          {/* Test Mode Visual Indicator */}
                          {testMode && (
                            <Alert className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-900">
                              <TestTube2 className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
                              <AlertDescription className="text-yellow-800 dark:text-yellow-200">
                                <strong>Test Modus aktiv:</strong> Post wird freigegeben aber NICHT veröffentlicht.
                              </AlertDescription>
                            </Alert>
                          )}

                          {/* Action Buttons — hidden in readOnly (DFY items are locked after
                              email approval; this surface is view-only). */}
                          {!readOnly && (
                          <div className="flex flex-wrap items-center justify-end gap-2 pt-4 border-t">
                            {isEditing ? (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleCancelEdit}
                                  disabled={editPostMutation.isPending}
                                  data-testid="button-cancel-edit"
                                >
                                  <X className="h-4 w-4 mr-2" />
                                  Abbrechen
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleSaveEdit(post)}
                                  disabled={editPostMutation.isPending}
                                  data-testid="button-save-edit"
                                >
                                  {editPostMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <Check className="h-4 w-4 mr-2" />
                                  )}
                                  Speichern
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleEdit(post)}
                                  data-testid="button-edit-post"
                                  title="Bearbeiten"
                                  aria-label="Post bearbeiten"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                {(post as any).postingStatus !== "posted" && (post as any).postingStatus !== "scheduled" && post.approvalStatus !== "rejected" && (
                                  <>
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      onClick={() => handleReject(post)}
                                      disabled={rejectPostMutation.isPending || publishPostMutation.isPending || schedulePostMutation.isPending}
                                      data-testid="button-reject-post"
                                    >
                                      <X className="h-4 w-4 mr-2" />
                                      Ablehnen
                                    </Button>

                                    {/* Schedule button with popover */}
                                    <SchedulePopover
                                      platform={post.platform || 'linkedin'}
                                      platformName={platformConfig[post.platform as keyof typeof platformConfig]?.name || post.platform}
                                      scheduledTimes={scheduledTimesByPlatform[post.platform] || []}
                                      onSchedule={(date) => handleSchedule(post, date)}
                                      isPending={schedulePostMutation.isPending}
                                    >
                                      <Button
                                        size="sm"
                                        className="bg-amber-500 hover:bg-amber-400 text-amber-950"
                                        disabled={schedulePostMutation.isPending || publishPostMutation.isPending}
                                        data-testid="button-schedule-post"
                                      >
                                        {schedulePostMutation.isPending ? (
                                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                          <CalendarClock className="h-4 w-4 mr-2" />
                                        )}
                                        Planen
                                      </Button>
                                    </SchedulePopover>

                                    <Button
                                      size="sm"
                                      onClick={() => requestPublish(post)}
                                      disabled={publishPostMutation.isPending || schedulePostMutation.isPending}
                                      data-testid="button-publish-post"
                                    >
                                      {publishPostMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      ) : (
                                        <Check className="h-4 w-4 mr-2" />
                                      )}
                                      Freigeben
                                    </Button>
                                  </>
                                )}
                                {post.approvalStatus === "approved" && (post as any).postingStatus !== "posted" && (post as any).postingStatus !== "scheduled" && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleReject(post)}
                                    disabled={rejectPostMutation.isPending}
                                    data-testid="button-withdraw-post"
                                  >
                                    <X className="h-4 w-4 mr-2" />
                                    Zurückziehen
                                  </Button>
                                )}
                                {post.approvalStatus === "rejected" && (
                                  <Button
                                    size="sm"
                                    onClick={() => requestPublish(post)}
                                    disabled={publishPostMutation.isPending}
                                    data-testid="button-reapprove-post"
                                  >
                                    {publishPostMutation.isPending ? (
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                      <Check className="h-4 w-4 mr-2" />
                                    )}
                                    Doch freigeben
                                  </Button>
                                )}
                                {((post as any).postingStatus === "posted" || (post as any).postingStatus === "scheduled") && (
                                  <>
                                    {/* Re-schedule */}
                                    <SchedulePopover
                                      platform={post.platform || 'linkedin'}
                                      platformName={platformConfig[post.platform as keyof typeof platformConfig]?.name || post.platform}
                                      scheduledTimes={scheduledTimesByPlatform[post.platform] || []}
                                      onSchedule={(date) => handleSchedule(post, date)}
                                      isPending={schedulePostMutation.isPending}
                                    >
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={schedulePostMutation.isPending || publishPostMutation.isPending}
                                      >
                                        {schedulePostMutation.isPending ? (
                                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                          <CalendarClock className="h-4 w-4 mr-2" />
                                        )}
                                        Neu planen
                                      </Button>
                                    </SchedulePopover>
                                    {/* Re-publish now */}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => requestPublish(post)}
                                      disabled={publishPostMutation.isPending || schedulePostMutation.isPending}
                                    >
                                      {publishPostMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      ) : (
                                        <RotateCw className="h-4 w-4 mr-2" />
                                      )}
                                      Erneut posten
                                    </Button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>
                  )
                })}
              </Tabs>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
    {/* Confirm before a live pillar publish (irreversible). Only armed for pillar posts on a
        real publish — see requestPublish; test-mode drafts and other sources bypass it. */}
    <AlertDialog open={!!pendingPublishPost} onOpenChange={(o) => { if (!o) setPendingPublishPost(null) }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Beitrag jetzt veröffentlichen?</AlertDialogTitle>
          <AlertDialogDescription>
            Der Beitrag wird sofort live veröffentlicht. Das lässt sich nicht rückgängig machen.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Abbrechen</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (pendingPublishPost) handlePublish(pendingPublishPost)
              setPendingPublishPost(null)
            }}
          >
            Veröffentlichen
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  )
}
